import { useEffect, useRef } from 'react';

/**
 * Hook to save and restore scroll position on page refresh.
 * Saves scroll position to sessionStorage and restores it when the component mounts.
 * 
 * @param key - Unique key for storing scroll position (e.g., 'chat-scroll')
 * @param containerRef - Reference to the scrollable container
 */
export function useScrollRestore(
  key: string,
  containerRef: React.RefObject<HTMLDivElement>,
  messages?: any[] // Pass messages to trigger restore when they load
) {
  const scrollTimeoutRef = useRef<NodeJS.Timeout>();
  const isRestoringRef = useRef(false);

  // Restore scroll position when messages load
  useEffect(() => {
    if (!messages || messages.length === 0) return;

    const container = (containerRef.current as any)?.container || (containerRef.current as HTMLDivElement);
    if (!container) return;

    // Only restore once per session load
    if (isRestoringRef.current) return;
    isRestoringRef.current = true;

    // Clear any inline padding from animation state before restore
    container.style.paddingBottom = '';

    const savedScrollPosition = sessionStorage.getItem(key);
    if (savedScrollPosition) {
      const scrollTop = parseInt(savedScrollPosition, 10);
      
      // Wait for DOM to be fully rendered and sized
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          container.scrollTop = scrollTop;
        });
      });
    }
  }, [key, containerRef, messages?.length]);

  // Save scroll position on scroll events (debounced)
  useEffect(() => {
    const container = (containerRef.current as any)?.container || (containerRef.current as HTMLDivElement);
    
    if (!container) return;

    const handleScroll = () => {
      // Clear existing timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      
      // Debounce scroll saves to avoid too many writes
      scrollTimeoutRef.current = setTimeout(() => {
        sessionStorage.setItem(key, container.scrollTop.toString());
      }, 500);
    };

    container.addEventListener('scroll', handleScroll);

    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [key, containerRef]);
}
